import React, { useEffect, useState } from "react";
import { ShieldOff, Square } from "lucide-react";
import { AppButton, AppModal, FormField, Input } from "../../ui";
import { toDateInput } from "./batchDetails";

/** Today in India as `yyyy-mm-dd` — the box and most browsers are not on IST. */
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

/**
 * EndBatchModal — confirm completing a running batch, optionally setting the
 * date students lose access to its content.
 *
 * Props:
 *   show, onHide
 *   batchName
 *   busy
 *   onConfirm(expiryDate) — "" when no expiry was chosen
 */
export const EndBatchModal = ({ show, onHide, batchName, busy = false, onConfirm }) => {
  const [expiry, setExpiry] = useState("");
  useEffect(() => { if (show) setExpiry(""); }, [show]);

  return (
    <AppModal
      show={show}
      onHide={busy ? undefined : onHide}
      title="End batch"
      icon={<Square size={18} />}
      size="sm"
      footer={(
        <>
          <AppButton size="sm" variant="outline" onClick={onHide} disabled={busy}>Cancel</AppButton>
          <AppButton size="sm" variant="destructive" onClick={() => onConfirm(expiry)} disabled={busy}>
            {busy ? "Ending…" : "End batch"}
          </AppButton>
        </>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-content">
        <p className="tw-m-0">
          Mark <strong>{batchName}</strong> as completed?
        </p>
        <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-[0.8125rem] tw-text-content-muted">
          <li>Its status changes to Completed.</li>
          <li>A live class running now is ended.</li>
          <li>Every student in it is notified.</li>
          <li>It leaves the calendar and the dashboard.</li>
          <li>It frees a batch slot on your plan.</li>
        </ul>
        <FormField
          label="Content expiry date (optional)"
          hint="Students keep access to the batch's content until this date. Leave it empty for no limit."
        >
          <Input type="date" value={expiry} min={todayIST()} onChange={(e) => setExpiry(e.target.value)} />
        </FormField>
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
          This cannot be undone here — to reopen the batch, edit its status.
        </p>
      </div>
    </AppModal>
  );
};

/**
 * ContentExpiryModal — set, change or clear the date students of a completed
 * batch lose access to its content.
 *
 * Props:
 *   show, onHide
 *   batch
 *   busy
 *   onSave(date) — "" clears the expiry
 */
export const ContentExpiryModal = ({ show, onHide, batch, busy = false, onSave }) => {
  const [date, setDate] = useState("");
  useEffect(() => { if (show) setDate(toDateInput(batch?.expiryDate)); }, [show, batch?.expiryDate]);

  return (
    <AppModal
      show={show}
      onHide={busy ? undefined : onHide}
      title="Content expiry"
      icon={<ShieldOff size={18} />}
      size="sm"
      footer={(
        <>
          <AppButton size="sm" variant="outline" onClick={onHide} disabled={busy}>Cancel</AppButton>
          <AppButton size="sm" onClick={() => onSave(date)} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </AppButton>
        </>
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm tw-text-content">
        <p className="tw-m-0">
          Students of <strong>{batch?.name}</strong> lose access to its content after this date.
        </p>
        <FormField label="Expiry date" hint="On or after the batch's end date. Clear it to remove the limit.">
          <Input
            type="date"
            value={date}
            min={toDateInput(batch?.endDate) || undefined}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>
      </div>
    </AppModal>
  );
};
